<template>
  <div
    class="text-[17px] leading-[18px] font-medium text-gray-800 pb-6 border-b border-[#DFDFDF]"
  >
    Subscriptions
  </div>

  <div class="border-b border-[#DFDFDF]">
    <SettingsRow class="h-auto! min-h-[70px] border-none py-[24px]">
      <template #left>
        <Skeleton v-if="loading" class="w-[150px] h-[18px]" />

        <div v-else class="flex">
          <DacrMarkIcon class="size-6 shrink-0 text-gray-800 mr-2.5" />

          <div>
            <div class="flex items-center gap-x-2.5 mb-0.5">
              <div class="font-medium text-gray-800">
                {{ billing?.plan.name }}
              </div>
              <Pill
                v-if="statusVariant"
                :variant="statusVariant"
                :icon="statusIcon"
              >
                {{ status }}
              </Pill>
            </div>
            <button
              v-if="isPro"
              type="button"
              class="text-link"
              @click="handlePlanDetailsClick"
            >
              See plan details
            </button>
          </div>
        </div>
      </template>

      <template #right>
        <Skeleton v-if="loading" class="w-[150px] h-[18px]" />

        <div v-else class="flex flex-col items-end text-right">
          <template v-if="prices.length">
            <div class="font-medium text-gray-800">
              <div v-for="(price, index) in prices" :key="`price-${index}`">
                <template v-if="price.details.amount">
                  {{ formatPriceLocal(price) }}
                </template>
                {{ t(price.details.suffix) }}
              </div>
            </div>

            <div v-if="showBillingDate" class="text-gray-800">
              {{ billing?.plan.dateInfo?.prefix }}
              {{ convertToFriendlyDate(billing?.plan.dateInfo?.date) }}
            </div>
          </template>

          <div v-else class="font-medium text-gray-800">Free</div>

          <div class="mt-3 flex flex-col items-end gap-y-3">
            <Button
              v-if="showResubscribeButton"
              variant="primary"
              :size="isMobile ? 'lg' : 'md'"
              :disabled="paymentsDisabled"
              @click="handleResubscribe"
            >
              Resubscribe
            </Button>

            <template v-else>
              <Button
                v-if="showUpgradeButton"
                variant="primary"
                :size="isMobile ? 'lg' : 'md'"
                :disabled="paymentsDisabled"
                @click="handleUpgradeClick"
              >
                Upgrade
              </Button>

              <button
                v-if="showChangePlan"
                type="button"
                class="text-link"
                :class="disabledClasses"
                @click="handleChangePlanClick"
              >
                Change plan
              </button>

              <Button
                v-if="showPayButton"
                variant="danger"
                :size="isMobile ? 'lg' : 'md'"
                :disabled="paymentsDisabled"
                @click="handlePayClick"
              >
                Pay balance
              </Button>
            </template>
          </div>
        </div>
      </template>
    </SettingsRow>

    <AlertBanner class="mb-[26px]" />
  </div>

  <SettingsRow class="h-auto! min-h-[70px] py-[24px]">
    <template #left>
      <div>Payment method</div>
    </template>

    <template #right>
      <Skeleton v-if="loading" class="w-[100px] h-[18px]" />

      <div v-else class="whitespace-nowrap">
        <div v-if="isWebSubscription" class="flex items-center justify-end">
          <template v-if="billing?.paymentMethod?.card">
            <CreditCard
              :type="billing.paymentMethod.card.type"
              class="mr-2.5"
            />
            <div>•••• •••• •••• {{ billing?.paymentMethod?.card?.last4 }}</div>
          </template>

          <button
            v-if="!webviewStore.isActive"
            type="button"
            class="ml-5 text-link"
            :class="disabledClasses"
            @click="handleUpdatePaymentMethod"
          >
            {{ billing?.paymentMethod?.card ? 'Change' : 'Add' }}
          </button>

          <div v-else-if="!billing?.paymentMethod?.card">—</div>
        </div>

        <div v-else-if="platform && isPro" class="flex items-center">
          <component
            :is="platformIcon"
            v-if="platformIcon"
            class="size-[18px] mr-2"
          />
          {{ platform }} in-app purchase
        </div>

        <div v-else>—</div>
      </div>
    </template>
  </SettingsRow>

  <SettingsRow
    class="h-auto! min-h-[70px] py-[24px]"
    :class="{ 'mb-[50px]': !showCancelRow }"
  >
    <template #left>
      <div class="flex flex-col w-2/3">
        <div class="mb-1">Next billings</div>
        <div class="text-[13px] leading-4 text-gray-500">
          Estimated charges are based on the number of your registrations
          currently on Dacr
        </div>
      </div>
    </template>

    <template #right>
      <Skeleton v-if="loading" class="w-[100px] h-[18px]" />

      <div v-else class="flex flex-col items-end text-right">
        <template v-if="billing?.upcomingDetails.upcomingBilling">
          <div class="mb-4">
            <div class="text-gray-800">
              Due
              {{
                convertToFriendlyDate(
                  billing.upcomingDetails.upcomingBilling.date
                )
              }}
            </div>
            <div class="font-medium text-gray-800">
              {{ upcomingBillingTotal }}
            </div>
          </div>

          <button
            type="button"
            class="text-link"
            @click="handleUpcomingBillingDetailsClick"
          >
            See details
          </button>
        </template>

        <div v-else>—</div>
      </div>
    </template>
  </SettingsRow>

  <SettingsRow
    v-if="showCancelRow"
    class="h-auto! min-h-[70px] py-[24px] mb-[50px]"
  >
    <template #left>
      <div class="flex flex-col w-2/3">
        <div class="mb-1">Cancel auto-renewal</div>
        <div class="text-[13px] leading-4 text-gray-500">
          Canceling your membership stops renewal, ends your ability to register
          new works and access to membership benefits.
        </div>
      </div>
    </template>

    <template #right>
      <button
        type="button"
        class="self-start text-link"
        @click="handleCancelPlanClick"
      >
        Cancel
      </button>
    </template>
  </SettingsRow>

  <div>
    <div class="text-[17px] leading-[18px] font-medium text-gray-800 mb-1.5">
      Billing history
    </div>
    <div class="text-[13px] leading-4 text-gray-500 mb-[27px]">
      View invoices and receipts for all payments made on your Dacr account
    </div>

    <InvoicesTable
      :invoices="billing?.invoices.invoices"
      :has-more="!!billing?.invoices.next"
      @fetch-more="handleFetchMoreInvoices"
    />
  </div>
</template>

<script setup lang="ts">
import {
  BillingAlertType,
  BillingPriceType,
  PlanStatus,
  PlanType,
  PlatformType,
  useBillingQueryQuery,
  UserPermission,
  type BillingPrice,
} from '@/shared/graphql/legacy/generated'
import { computed } from 'vue'
import SettingsRow from '@/features/settings/components/SettingsRow.vue'
import { t } from '@/shared/i18n'
import { formatPrice } from '@/shared/utils/money'
import { formatTimestamp } from '@/shared/utils/datetime'
import { Button, Pill, Skeleton, type PillVariant } from '@dacr/library'
import { DacrMarkIcon, AppleIcon, GoogleIcon, AlertIcon } from '@dacr/icons'
import CreditCard from '@/features/billing/components/CreditCard.vue'
import InvoicesTable from '@/features/billing/components/InvoicesTable.vue'
import AlertBanner from '@/shared/components/AlertBanner.vue'
import { useWebviewStore } from '@/shared/stores/webview'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'
import { capitalize } from '@/shared/utils/string'
import { useBillingStore } from '@/features/billing/store'
import { useRouter } from 'vue-router'
import { openModal, closeModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'

const { result, fetchMore, loading } = useBillingQueryQuery()

const { isMobile } = useDevice()
const router = useRouter()
const billingStore = useBillingStore()
const webviewStore = useWebviewStore()
const userStore = useUserStore()

const billing = computed(() => result.value?.billing)
const user = computed(() => userStore.user)

const convertToFriendlyDate = (date?: number) => {
  if (!date) return ''
  return formatTimestamp(date, { formatString: 'LLLL d, yyyy' })
}

const isPro = computed(() =>
  user.value?.permissions.includes(UserPermission.Pro)
)

const isActiveSubscription = computed(
  () => billing.value?.plan.status === PlanStatus.Active
)

const isWebSubscription = computed(
  () => billing.value?.plan.platform === PlatformType.Web
)

const platform = computed(() => capitalize(billing.value?.plan.platform || ''))

const platformIcon = computed(() => {
  if (billing.value?.plan.platform === PlatformType.Apple) return AppleIcon
  if (billing.value?.plan.platform === PlatformType.Google) return GoogleIcon
  return null
})

const paymentsDisabled = computed(
  () => !user.value?.permissions.includes(UserPermission.Payments)
)

const disabledClasses = computed(() => ({
  'opacity-50 pointer-events-none': paymentsDisabled.value,
}))

const paymentRequiresAction = computed(
  () => billing.value?.plan.status === PlanStatus.RequiresAction
)

const hasBundlePlan = computed(
  () => billing.value?.plan.type === PlanType.Bundle
)

const showUpgradeButton = computed(
  () =>
    !!user.value?.canUpgradePlan && !hasBundlePlan.value && !prices.value.length
)

const showChangePlan = computed(
  () =>
    prices.value.length > 0 &&
    isActiveSubscription.value &&
    !!user.value?.canChangePlan
)

const isCanceled = computed(
  () => billing.value?.plan.status === PlanStatus.Canceled
)

// A cancelled subscription can still owe: cancelling doesn't settle the balance, and its
// registration charges keep running. Such a user gets "Pay balance", not "Resubscribe" —
// settling comes first, and `plan.status` reports Canceled rather than Overdue for them.
const hasOutstandingBalance = computed(
  () => (user.value?.upcomingBilling?.outstandingTotal ?? 0) > 0
)

const showPayButton = computed(
  () =>
    billing.value?.plan.status === PlanStatus.Overdue ||
    (isCanceled.value && hasOutstandingBalance.value)
)

const showResubscribeButton = computed(
  () => isCanceled.value && !hasOutstandingBalance.value
)

const isChargeback = computed(
  () => billing.value?.billingAlert?.type === BillingAlertType.Chargeback
)

const showCancelRow = computed(
  () =>
    prices.value.length > 0 &&
    !isCanceled.value &&
    !isChargeback.value &&
    !paymentsDisabled.value
)

const formatPriceLocal = (price: BillingPrice) => {
  return formatPrice({
    amount: (price.details.amount || 0) / 100,
    currency: price.details.currency,
  })
}

const upcomingBillingTotal = computed(() => {
  return formatPrice({
    amount: (user.value?.upcomingBilling?.total || 0) / 100,
    currency: user.value?.upcomingBilling?.currency,
  })
})

const prices = computed(() => {
  const allPrices = billing.value?.plan?.prices || []
  return allPrices.filter((price) => !!price.details.amount)
})

const status = computed(() => {
  if (billing.value?.plan.status === PlanStatus.Canceled) return 'Cancelled'
  if (billing.value?.plan.status === PlanStatus.Overdue) return 'Overdue'
  if (billing.value?.plan.status === PlanStatus.RequiresAction)
    return 'Requires action'
  return ''
})

const statusVariant = computed<PillVariant | undefined>(() => {
  if (paymentRequiresAction.value) return 'warning'
  if (status.value) return 'danger'
  return undefined
})

const statusIcon = computed(() =>
  isCanceled.value || hasOutstandingBalance.value ? AlertIcon : undefined
)

const hasSubscriptionFee = computed(
  () =>
    !!billing.value?.plan.prices.find(
      (price) => price.type === BillingPriceType.Subscription
    )?.details.amount
)

const showBillingDate = computed(() => {
  const plan = billing.value?.plan
  if (!plan?.dateInfo?.date || plan.status === PlanStatus.Overdue) return false
  return plan.status !== PlanStatus.Active || hasSubscriptionFee.value
})

const handlePayClick = () => {
  if (!billingStore.canUpdateSubscription && webviewStore.isActive) {
    return openDialog(Dialog.CantPay)
  }

  openModal(Modals.Pay, {
    events: {
      success() {
        openModal(Modals.Success, {
          props: { title: 'Payment successful' },
          events: {
            success() {
              closeModal()
            },
          },
        })
      },
    },
  })
}

const handleChangePlanClick = () => {
  if (!billingStore.canUpdateSubscription) {
    return openDialog(Dialog.IAP, {
      props: { type: 'change' },
    })
  }
  router.push('/change-plans')
}

const handleUpgradeClick = () => {
  if (!billingStore.canUpdateSubscription) {
    return openDialog(Dialog.IAP, {
      props: { type: 'change' },
    })
  }
  router.push('/upgrade')
}

const handlePlanDetailsClick = () => {
  if (!billing.value) return
  openDialog(Dialog.PlanDetails, {
    props: { prices: billing.value.plan.details.prices },
  })
}

const handleUpcomingBillingDetailsClick = () => {
  if (!billing.value) return
  openDialog(Dialog.UpcomingBillingDetails, {
    props: { details: billing.value.upcomingDetails },
  })
}

const handleUpdatePaymentMethod = () => {
  if (!billingStore.canUpdateSubscription) {
    return openDialog(Dialog.IAP, {
      props: { type: 'change' },
    })
  }
  openDialog(Dialog.AddPaymentMethod)
}

const handleCancelPlanClick = () => {
  if (!billingStore.canUpdateSubscription && !webviewStore.isActive) {
    return openDialog(Dialog.IAP, { props: { type: 'cancel' } })
  }
  openDialog(Dialog.CancelPlan)
}

const handleResubscribe = () => {
  if (!billingStore.canUpdateSubscription && webviewStore.isActive) {
    return openDialog(Dialog.ResubscribeIAP)
  }
  openModal(Modals.Resubscribe)
}

const handleFetchMoreInvoices = () => {
  fetchMore({
    variables: { nextInvoice: billing.value?.invoices.next },
    updateQuery: (previousData, { fetchMoreResult }) => {
      if (!fetchMoreResult) return previousData

      return {
        ...previousData,
        billing: {
          ...previousData.billing,
          invoices: {
            __typename: 'InvoicesResponse' as const,
            invoices: previousData.billing.invoices.invoices.concat(
              fetchMoreResult.billing.invoices.invoices
            ),
            next: fetchMoreResult.billing.invoices.next,
          },
        },
      }
    },
  })
}
</script>
